import {
  Filter, LayoutDashboard, Megaphone, TrendingUp, Palette, Sparkles, Mail, Settings,
  Landmark, MessageSquare, FlaskConical, PanelsTopLeft, BookOpenText, Rocket, LucideIcon, Handshake, Clapperboard, Newspaper, Building2 } from 'lucide-react';
import type { useRole } from '../lib/useRole';

type RoleState = ReturnType<typeof useRole>;

/**
 * Which capsule a tab sits in. The growth command center splits its tab row
 * into "how we're acquiring" and "how the business is doing", so the two kinds
 * of question never sit in the same list — this mirrors that.
 *
 * `more` items collapse into the tab row's dropdown (Cody 2026-08-26: the big
 * three are Journal, Landing, Scaling; the reference dashboards step back).
 * `menu` items don't render as tabs at all; they're reachable from the account
 * menu and the command palette.
 */
export type NavGroup = 'channels' | 'business' | 'more' | 'menu';

export interface NavItem {
  path: string;
  label: string;
  /** One-line "what this is for" — shown in the command palette. */
  hint: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Extra words the command palette matches on. */
  keywords?: string[];
  /** Undefined = everyone signed in. Mirrors the route guards in App.tsx. */
  allowed?: (role: RoleState) => boolean;
  /** Which live count the tab row shows as a badge next to the label. */
  badge?: 'creatorsPending' | 'affiliatesPending';
}

export const NAV_ITEMS: NavItem[] = [
  {
    path: '/',
    label: 'Overview',
    hint: 'Spend, reach and conversions across every campaign',
    icon: LayoutDashboard,
    group: 'more',
    keywords: ['dashboard', 'home', 'spend', 'overview'],
  },
  {
    path: '/campaigns',
    label: 'Campaigns',
    hint: 'Per-campaign Meta performance and daily trends',
    icon: Megaphone,
    group: 'more',
    keywords: ['meta', 'facebook', 'insights'],
  },
  {
    path: '/ads',
    label: 'Ads',
    hint: 'Prospecting decisions on database truth — CPA, bench, operator',
    icon: TrendingUp,
    group: 'channels',
    keywords: ['cpa', 'prospecting', 'operator', 'bench', 'testing'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/scaling',
    label: 'Scaling',
    hint: 'Gold concepts to flood — cards with production prompts, auto-closed when variants land',
    icon: Rocket,
    group: 'channels',
    keywords: ['gold', 'kanban', 'saturate', 'variants', 'pipeline'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/testing',
    label: 'Testing',
    hint: 'Fresh ad tests vs the kill thresholds — keep/kill calls, cost/signup, CTR, watch time',
    icon: FlaskConical,
    group: 'channels',
    keywords: ['testing', 'launches', 'new ads', 'kill', 'keep', 'threshold', 'watch', 'hook', 'monitor', 'guard'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/journal',
    label: 'Journal',
    hint: 'Plain-English feed of every action on the ad account — kill switch, guardian, your edits',
    icon: BookOpenText,
    group: 'channels',
    keywords: ['journal', 'log', 'history', 'decisions', 'reasoning', 'kill switch', 'audit'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/landing',
    label: 'Landing',
    hint: 'Landing pages ads point at — funnel per page, keep/kill (kill pauses the ads)',
    icon: PanelsTopLeft,
    group: 'channels',
    keywords: ['landing', 'pages', 'slug', 'variant', 'template', 'funnel', 'quiz rate', 'kill', 'keep'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    // The clip editor gets its own tab (Cody 2026-09-22): it's where ads get
    // cut from the b-roll shelf, not a reference dashboard behind "More".
    path: '/creative/storyboard',
    label: 'Storyboard',
    hint: 'Clip shelf and beat timeline — cut a VO ad from the b-roll library, send it to Claude to render',
    icon: Clapperboard,
    group: 'channels',
    keywords: ['storyboard', 'clips', 'b-roll', 'editor', 'shelf', 'beats', 'render', 'library'],
    allowed: (r) => r.hasCreativeAccess,
  },
  {
    path: '/creative',
    label: 'Creative',
    hint: 'Concepts, copy, photo library and competitor intelligence',
    icon: Palette,
    group: 'more',
    keywords: ['concepts', 'copy', 'photos', 'composer', 'intel'],
    allowed: (r) => r.hasCreativeAccess,
  },
  {
    path: '/reddit',
    label: 'Reddit',
    hint: 'Friend-seeking threads found, drafted and approved before posting',
    icon: MessageSquare,
    group: 'more',
    keywords: ['organic', 'threads', 'replies', 'queue', 'subreddit'],
    allowed: (r) => r.hasCreativeAccess,
  },
  {
    path: '/creators',
    label: 'Creators',
    hint: 'UGC clip program — review queue, creators, library, payouts; partnerships tab for deals',
    icon: Sparkles,
    group: 'more',
    keywords: ['ugc', 'clips', 'queue', 'library', 'payouts', 'influencer', 'deals', 'partners', 'portal'],
    allowed: (r) => r.hasCreativeAccess,
    badge: 'creatorsPending',
  },
  {
    path: '/affiliates',
    label: 'Affiliates',
    hint: 'Affiliate program — applications, the roster with Rewardful numbers, prospects, inbox',
    icon: Handshake,
    group: 'more',
    keywords: ['affiliate', 'rewardful', 'referral', 'partners', 'links', 'commission'],
    allowed: (r) => r.hasCreativeAccess,
    badge: 'affiliatesPending',
  },
  {
    path: '/press',
    label: 'Press',
    hint: 'Earned media — outlets to pitch, releases, every touch logged, follow-ups due',
    icon: Newspaper,
    group: 'more',
    keywords: ['press', 'pr', 'media', 'journalists', 'release', 'pitch', 'coverage', 'outlets'],
    allowed: (r) => r.hasCreativeAccess,
  },
  {
    path: '/venues',
    label: 'Venues',
    hint: 'Outclose Groups venue relations — find activity venues, email them from venues@, approve every reply',
    icon: Building2,
    group: 'more',
    keywords: ['venues', 'groups', 'climbing', 'gym', 'pottery', 'boxing', 'pickleball', 'book club', 'partners'],
    allowed: (r) => r.isAdmin,
  },
  {
    path: '/funnel',
    label: 'Funnel',
    hint: 'Where each audience bucket leaks, what to test next, and every test in flight',
    icon: Filter,
    group: 'business',
    keywords: ['funnel', 'conversion', 'quiz', 'buckets', 'tests', 'operator', 'cro', 'experiments', 'ab test', 'launch', 'verdict'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/lifecycle',
    label: 'Lifecycle',
    hint: 'Customer.io flows, funnel coverage and email alerts',
    icon: Mail,
    group: 'business',
    keywords: ['email', 'customer.io', 'cio', 'campaigns', 'funnel'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    path: '/financial',
    label: 'Financial',
    hint: 'MRR, churn, LTV/CAC and daily cashflow on database truth',
    icon: Landmark,
    group: 'business',
    keywords: ['mrr', 'arr', 'churn', 'ltv', 'cac', 'cashflow', 'burn', 'subscriptions', 'money'],
    allowed: (r) => r.hasAnalyticsAccess,
  },
  {
    // Reachable from the account menu and ⌘K. Keeping it out of the tab row
    // leaves the two capsules to the dashboards themselves.
    path: '/settings',
    label: 'Settings',
    hint: 'Facebook sync, sync history and your session',
    icon: Settings,
    group: 'menu',
    keywords: ['sync', 'account', 'sign out', 'session'],
  },
];

/**
 * Everything this account can open, including menu-only destinations. The
 * command palette wants this list; the tab row wants `navGroups`.
 *
 * While the role claim is still loading we show everything rather than
 * flashing items away a moment after paint.
 */
export function visibleNavItems(role: RoleState): NavItem[] {
  if (role.loading) return NAV_ITEMS;
  return NAV_ITEMS.filter((item) => !item.allowed || item.allowed(role));
}

/** The big three lead the acquisition capsule; the working boards follow. */
const CHANNEL_ORDER = ['/journal', '/landing', '/scaling', '/ads', '/testing', '/creative/storyboard'];

/**
 * The tab row's capsules, in order: acquisition, business, then the reference
 * dashboards. Every panel sits in the row and the row scrolls sideways, the
 * way the main app's admin nav does (Cody 2026-10-20); nothing hides behind a
 * menu. Empty groups are dropped.
 */
export function navGroups(role: RoleState): NavItem[][] {
  const visible = visibleNavItems(role);
  return (['channels', 'business', 'more'] as const)
    .map((group) => {
      const items = visible.filter((item) => item.group === group);
      if (group === 'channels') {
        items.sort((a, b) => CHANNEL_ORDER.indexOf(a.path) - CHANNEL_ORDER.indexOf(b.path));
      }
      return items;
    })
    .filter((group) => group.length > 0);
}

/** The tab row's dropdown — reference dashboards that stepped back. */
export function navMore(role: RoleState): NavItem[] {
  return visibleNavItems(role).filter((item) => item.group === 'more');
}
